import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { deleteDocumentFolder, DOCUMENT_ENTITY_TYPES } from "services/documentFolderService";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { Listbox } from "components/shared/form/Listbox";

/**
 * Modal to confirm soft deletion of a folder and its contents using ConfirmModal
 */
export function DeleteFolderModal({
  isOpen,
  onClose,
  onDeleteSuccess,
  folder,
  availableFolders = [],
  entityType = null,
  entityId = null,
  enableSetDefaultFolder = false,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDefaultId, setSelectedDefaultId] = useState("");
  const [allFolders, setAllFolders] = useState([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);

  const isDefaultFolder = Boolean(
    (entityType === DOCUMENT_ENTITY_TYPES.JOB || enableSetDefaultFolder) &&
    (folder?.IsDefault || folder?.isDefault)
  );
  
  // Use allFolders if fetched, otherwise fallback to availableFolders
  const foldersToUse = allFolders.length > 0 ? allFolders : (availableFolders || []);
  const eligibleFolders = foldersToUse.filter(
    (f) => String(f.FolderId || f.id) !== String(folder?.FolderId || folder?.id) &&
           String(f.ParentFolderId || f.parentFolderId) !== String(folder?.FolderId || folder?.id) // roughly filter out immediate children
  );

  useEffect(() => {
    let isMounted = true;
    const fetchAllFolders = async () => {
      if (!isOpen || !isDefaultFolder || !entityId) return;
      setIsLoadingFolders(true);
      const fetchedFolders = [];
      const fetchLevel = async (folderId) => {
        try {
          // getDocumentFolders is imported from services/documentFolderService
          const { getDocumentFolders } = await import("services/documentFolderService");
          const res = await getDocumentFolders({ entityType, entityId, folderId, pageSize: 1000 });
          if (res.success && res.folders && res.folders.length > 0) {
            for (const f of res.folders) {
              fetchedFolders.push(f);
              await fetchLevel(f.FolderId || f.id);
            }
          }
        } catch (err) {
          console.error("Error fetching folders recursively", err);
        }
      };
      await fetchLevel(null);
      if (isMounted) {
        setAllFolders(fetchedFolders);
        setIsLoadingFolders(false);
        if (fetchedFolders.length > 0) {
          const first = fetchedFolders.filter(f => String(f.FolderId || f.id) !== String(folder?.FolderId || folder?.id))[0];
          if (first) setSelectedDefaultId(String(first.FolderId || first.id));
        }
      }
    };

    if (isOpen) {
      if (isDefaultFolder && entityId) {
        fetchAllFolders();
      } else {
        if (isDefaultFolder && eligibleFolders.length > 0) {
          const first = eligibleFolders[0];
          setSelectedDefaultId(String(first.FolderId || first.id));
        } else {
          setSelectedDefaultId("");
        }
      }
    }
    return () => { isMounted = false; };
  }, [isOpen, isDefaultFolder, entityId, entityType]);

  const handleDelete = async () => {
    const targetFolderId = folder?.FolderId || folder?.id;
    if (!targetFolderId) return;

    if (isDefaultFolder && !selectedDefaultId) {
      toast.error("Please select a new default folder before deleting");
      return;
    }

    setIsSubmitting(true);
    try {
      const finalNewDefault = selectedDefaultId === "root" ? null : selectedDefaultId;
      const res = await deleteDocumentFolder({
        folderId: targetFolderId,
        newDefaultFolderId: isDefaultFolder ? finalNewDefault : null,
      });
      if (res.success) {
        toast.success(res.message || "Folder deleted successfully");
        onClose();
        if (onDeleteSuccess) onDeleteSuccess(targetFolderId);
      } else {
        toast.error(res.error || "Failed to delete folder");
      }
    } catch (err) {
      toast.error(err.message || "Error deleting folder");
    } finally {
      setIsSubmitting(false);
    }
  };

  const descriptionContent = isDefaultFolder ? (
    <div className="space-y-2 text-left mt-0.5">
      <p className="text-xs text-gray-500 dark:text-dark-300 text-center">
        Are you sure you want to delete &quot;{folder?.Name || folder?.name}&quot;? All subfolders and documents inside it will also be deleted.
      </p>
      <div className="rounded-lg bg-amber-50 p-2.5 text-left border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
        <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-0.5">
          Default Folder Reassignment
        </p>
        <p className="text-[11px] text-amber-700 dark:text-amber-400 mb-1.5">
          Select a replacement default folder:
        </p>
        <Listbox
          value={selectedDefaultId}
          onChange={(newVal) => setSelectedDefaultId(newVal)}
          disabled={isLoadingFolders}
          className="w-full text-xs"
          placeholder={isLoadingFolders ? "Loading folders..." : "-- Select a new default folder --"}
          data={[
            {
              value: "root",
              label: "Root Folder (General)"
            },
            ...eligibleFolders.map((f) => ({
              value: f.FolderId || f.id,
              label: f.Name || f.name
            }))
          ]}
          multiple={false}
          closeOnSelect={true}
          placement="bottom"
        />
      </div>
    </div>
  ) : (
    `Are you sure you want to delete "${folder?.Name || folder?.name || "this folder"}"? All subfolders and documents inside it will also be deleted. This action cannot be undone.`
  );

  return (
    <ConfirmModal
      show={Boolean(isOpen)}
      onClose={onClose}
      onOk={handleDelete}
      confirmLoading={isSubmitting}
      state="pending"
      buttonColor="error"
      messages={{
        pending: {
          title: `Delete "${folder?.Name || folder?.name || "Folder"}"?`,
          description: descriptionContent,
          actionText: "Delete",
        },
        success: {
          title: "Folder Deleted",
          description: "The folder has been successfully deleted.",
          actionText: "Done",
        },
        error: {
          title: "Delete Failed",
          description: "Failed to delete the folder. Please try again.",
          actionText: "Retry",
        },
      }}
    />
  );
}

export default DeleteFolderModal;
